import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import { baseUrl, uploadFile } from "../../../Utils/FileUpload";
import {
  Submit,
  AddLanguageTitle,
  LanguageTitle,
} from "../../../Utils/Constants";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import useCustomToasts from "../../../hooks/useCustomToast";

interface FormValues {
  language: string;
  icon: FileList;
}
interface ErrorResponse {
  message: string;
}

export default function AddLanguage() {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      const file = data.icon?.[0];

      if (!file) {
        toast.error("Please upload a icon.");
        return;
      }

      const fileName = await uploadFile(file);

      const payload = {
        ...data,
        icon: fileName,
      };

      const token = localStorage.getItem("token");

      const response = await axios.post(`${baseUrl}language/store`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        SuccessToast("Data added successfully", "/view_language");
      } else {
        ErrorToast("Failed to add");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage =
        (axiosError.response?.data as ErrorResponse)?.message ||
        "An unexpected error occurred";

      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 2000);
    }
  };

  // Prevent form submission on Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={AddLanguageTitle}
        parent={LanguageTitle}
        title={AddLanguageTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="g-3">
                <Col xs={12}>
                  <Label htmlFor="language-name">Language</Label>
                  <Controller
                    name="language"
                    control={control}
                      rules={{
                        required: "Language is required",
                          maxLength: {
    value: 25,
    message: "Language cannot be more than 25 characters.",
  },
                        pattern: {
                          value: /^[A-Za-z\s\W_]+$/,
                          message:
                            "Only letters, spaces, and symbols are allowed. Numbers are not allowed.",
                        },
                        validate: (value) => {
                          if (/\d/.test(value)) {
                            return "Numbers are not allowed.";
                          }
                          if (value.trim() === "") {
                            return "Language cannot be empty or only spaces.";
                          }
                          return true;
                        },
                      }}
                    render={({ field }) => (
                      <>
                        <Input
                          id="language-name"
                          type="text"
                          placeholder="Language"
                          {...field}
                          invalid={!!errors.language}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.language ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.language && (
                          <FormFeedback>{errors.language.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="formFile">Icon</Label>
                  <Controller
                    name="icon"
                    control={control}
                    rules={{ required: "Icon is required" }}
                    render={({ field: { onChange, onBlur, ref } }) => (
                      <>
                        <Input
                          id="formImage"
                          type="file"
                          accept="image/*"
                          onChange={(e) => onChange(e.target.files)}
                          onBlur={onBlur}
                          innerRef={ref}
                          invalid={!!errors.icon}
                          style={{ borderColor: errors.icon ? "red" : "" }}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.icon && (
                          <FormFeedback>{errors.icon.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12} className="mt-2">
                  <Btn color="primary" disabled={isButtonDisabled}>
                    {Submit}
                  </Btn>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer
        toastStyle={{
          userSelect: "none",
        }}
      />
    </>
  );
}
